/**
 * In-app / embedded browser (webview) detection.
 *
 * Why this exists: Google's "Use secure browsers" policy refuses to run its
 * OAuth consent screen inside an app's embedded webview (LinkedIn, Gmail,
 * Slack, Instagram, Messenger, LINE, …). A user who opens our signup/login
 * link from inside one of those apps and taps "Continue with Google" slams
 * into Google's raw `Error 403: disallowed_useragent` before the flow ever
 * reaches our infrastructure. We detect the webview client-side and steer the
 * user to a path that works (email / magic-code, or opening a real browser).
 *
 * This is a deliberately hand-rolled detector rather than a dependency
 * (`detect-inapp`, `inapp-spy`): we need canonical app-source names for
 * analytics, we don't want a new runtime dependency on the auth-critical path,
 * and the heuristic is small and fully unit-tested (see `__tests__`).
 */

export type InAppBrowserPlatform = "ios" | "android" | "other";

export interface InAppBrowserDetection {
  /** True when the UA looks like an embedded/in-app browser (webview). */
  isInApp: boolean;
  /** Human-readable app name when identifiable (e.g. "LinkedIn"), else null. */
  source: string | null;
  /** Best-effort OS platform — drives which escape hatch the notice offers. */
  platform: InAppBrowserPlatform;
}

/**
 * App-specific UA tokens, most-specific first. Order matters: Messenger's UA
 * also carries an `FBAN/` token, so it must be matched before Facebook.
 */
const APP_SOURCE_MATCHERS: ReadonlyArray<{ name: string; pattern: RegExp }> = [
  { name: "Messenger", pattern: /MessengerForiOS|Orca-Android|\bFBAN\/Messenger/i },
  { name: "Facebook", pattern: /\bFBAN\/|\bFBAV\/|\bFB_IAB\b|\bFB4A\b/i },
  { name: "Instagram", pattern: /\bInstagram\b/i },
  { name: "LINE", pattern: /\bLine\//i },
  // iOS token is bare `LinkedInApp`; Android/older builds version it as `LinkedIn/8.x`.
  { name: "LinkedIn", pattern: /\bLinkedInApp\b|\bLinkedIn\//i },
  // `Twitter for iPhone` (space) and `TwitterAndroid/9.x` (no boundary) both count.
  { name: "Twitter", pattern: /\bTwitter/i },
  { name: "Snapchat", pattern: /\bSnapchat\b/i },
  // iOS uses `Slack/23.x`; Android's standalone-browser build uses `Slack_SSB/4.x`.
  { name: "Slack", pattern: /\bSlack[_/]/i },
  { name: "WeChat", pattern: /\bMicroMessenger\b/i },
  { name: "Google App", pattern: /\bGSA\//i },
];

/**
 * Standalone iOS browsers that legitimately omit the `Safari` token. These are
 * NOT webviews — without this guard, Chrome/Firefox/Edge/Opera on iOS would be
 * misclassified as in-app browsers (they render their own UA without `Safari`).
 */
const IOS_STANDALONE_BROWSER = /CriOS|FxiOS|EdgiOS|OPiOS|mercury|DuckDuckGo|Brave/i;

function resolveUserAgent(ua?: string): string | null {
  if (typeof ua === "string") return ua;
  if (typeof navigator !== "undefined" && typeof navigator.userAgent === "string") {
    return navigator.userAgent;
  }
  return null;
}

function resolvePlatform(ua: string): InAppBrowserPlatform {
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

/**
 * Android System WebView signal: the `; wv)` marker every system-webview UA
 * carries (e.g. `(Linux; Android 14; Pixel 8; wv)`). We match only this anchored
 * form — a bare `wv` token would risk false-positives against unrelated UA
 * substrings, and any real webview includes the parenthesised marker.
 */
function isAndroidWebView(ua: string): boolean {
  return /;\s*wv\s*\)/i.test(ua);
}

/**
 * iOS webview signal: an iOS WebKit UA that carries the `Mobile` token but
 * lacks `Safari` and is not one of the known standalone iOS browsers.
 */
function isIosWebView(ua: string): boolean {
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  if (!isIos) return false;
  if (!/AppleWebKit/i.test(ua)) return false;
  if (IOS_STANDALONE_BROWSER.test(ua)) return false;
  return /Mobile\//i.test(ua) && !/Safari/i.test(ua);
}

/**
 * Inspect a user-agent string (or `navigator.userAgent` when omitted) and
 * report whether it belongs to an in-app/embedded browser, which app it is
 * (when identifiable), and the OS platform.
 *
 * Returns `{ isInApp: false, source: null, platform: "other" }` when no UA is
 * available (e.g. during SSR) — callers must run this after mount.
 */
export function detectInAppBrowser(ua?: string): InAppBrowserDetection {
  const userAgent = resolveUserAgent(ua);
  if (!userAgent) {
    return { isInApp: false, source: null, platform: "other" };
  }

  const platform = resolvePlatform(userAgent);

  const matchedApp = APP_SOURCE_MATCHERS.find((m) => m.pattern.test(userAgent));
  if (matchedApp) {
    return { isInApp: true, source: matchedApp.name, platform };
  }

  // No app-specific token, but the UA still looks like a generic webview.
  if (platform === "android" && isAndroidWebView(userAgent)) {
    return { isInApp: true, source: null, platform };
  }
  if (platform === "ios" && isIosWebView(userAgent)) {
    return { isInApp: true, source: null, platform };
  }

  return { isInApp: false, source: null, platform };
}

/** Convenience boolean wrapper around {@link detectInAppBrowser}. */
export function isInAppBrowser(ua?: string): boolean {
  return detectInAppBrowser(ua).isInApp;
}
